import type { Rect } from '../../../engine/hex/types';
import type { Painter } from '../rendering/Painter';
import { overlayTokens } from '../styles/tokens';
import type { DismissibleGroupVM, DismissibleItemVM } from '../vm/types';
import type { HitTarget } from './ActionBar';

export class DismissibleStack {
    render(painter: Painter, group: DismissibleGroupVM): HitTarget[] {
        const targets: HitTarget[] = [];
        const headerColors = resolveHeaderColors(group.kind);
        painter.panel(group.header.rect, headerColors.fill, headerColors.stroke);
        painter.text(
            group.header.title,
            group.header.rect.x + 8,
            group.header.rect.y + 4,
            headerColors.text,
            overlayTokens.fonts.base,
        );

        group.items.forEach((item) => {
            const itemColors = resolveItemColors(item);
            painter.panel(item.rect, itemColors.fill, itemColors.stroke);

            if (item.kind === 'notification') {
                painter.text(
                    item.title,
                    item.rect.x + 8,
                    item.rect.y + 6,
                    overlayTokens.colors.text,
                    overlayTokens.fonts.small,
                );
                painter.text(
                    item.detail,
                    item.rect.x + 8,
                    item.rect.y + 24,
                    overlayTokens.colors.textMuted,
                    overlayTokens.fonts.detail,
                );
            } else {
                item.detailLines.forEach((line, index) => {
                    painter.text(
                        line,
                        item.rect.x + 8,
                        item.rect.y + 8 + index * 12,
                        itemColors.text,
                        overlayTokens.fonts.small,
                    );
                });
            }

            if (item.progress) {
                const barRect = getProgressRect(item.rect);
                painter.progressBar(
                    barRect,
                    item.progress.ratio,
                    overlayTokens.colors.progressTrackFill,
                    overlayTokens.colors.progressTrackStroke,
                    overlayTokens.colors.progressFill,
                );
            }

            if (item.action) {
                painter.button(
                    item.action.rect,
                    overlayTokens.colors.buttonInactiveFill,
                    overlayTokens.colors.buttonInactiveStroke,
                );
                painter.text(
                    item.action.label,
                    item.action.rect.x + item.action.rect.w / 2,
                    item.action.rect.y + 2,
                    overlayTokens.colors.text,
                    overlayTokens.fonts.tiny,
                    'center',
                );
                targets.push({
                    id: item.action.id,
                    rect: item.action.rect,
                    enabled: item.action.enabled,
                });
            }

            targets.push({ id: item.id, rect: item.dismissRect, enabled: true });
            painter.button(
                item.dismissRect,
                overlayTokens.colors.buttonInactiveFill,
                overlayTokens.colors.buttonInactiveStroke,
            );
            painter.text(
                '×',
                item.dismissRect.x + item.dismissRect.w / 2,
                item.dismissRect.y + 3,
                overlayTokens.colors.text,
                overlayTokens.fonts.tiny,
                'center',
            );
        });

        return targets;
    }
}

function resolveHeaderColors(kind: DismissibleGroupVM['kind']): {
    fill: string;
    stroke: string;
    text: string;
} {
    if (kind === 'alert') {
        return {
            fill: overlayTokens.colors.errorFill,
            stroke: overlayTokens.colors.errorStroke,
            text: overlayTokens.colors.errorText,
        };
    }
    return {
        fill: overlayTokens.colors.panelFill,
        stroke: overlayTokens.colors.panelStroke,
        text: overlayTokens.colors.text,
    };
}

function resolveItemColors(item: DismissibleItemVM): {
    fill: string;
    stroke: string;
    text: string;
} {
    if (item.kind === 'notification') {
        return {
            fill: overlayTokens.colors.panelFill,
            stroke: overlayTokens.colors.panelStroke,
            text: overlayTokens.colors.text,
        };
    }
    if (item.severity === 'warn') {
        return {
            fill: overlayTokens.colors.warnFill,
            stroke: overlayTokens.colors.warnStroke,
            text: overlayTokens.colors.warnText,
        };
    }
    return {
        fill: overlayTokens.colors.errorFill,
        stroke: overlayTokens.colors.errorStroke,
        text: overlayTokens.colors.errorText,
    };
}

function getProgressRect(itemRect: Rect): Rect {
    const { progress } = overlayTokens.layout;
    return {
        x: itemRect.x + progress.paddingX,
        y: itemRect.y + progress.gapY,
        w: itemRect.w - progress.rightInset,
        h: progress.height,
    };
}
